import React, { useContext, useEffect, useRef } from 'react';
import {
  Button,
  ButtonType,
  Drawer
} from 'syngenta-digital-cropwise-react-ui-kit';
import { Formik, useFormikContext, FormikProps } from 'formik';
import { datadogLogs } from '@datadog/browser-logs';
import ChevronLeftIcon from 'assets/chevron_left.svg?react';
import CloseIcon from 'assets/close.svg?react';
import { FormValues } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { ProductWithPlanFields } from 'types/product';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import usePreferences, { PreferenceKey } from 'hooks/usePreferences';
import { Context } from 'components/Store';
import useHandlePreferencesErrorToast from 'hooks/useHandlePreferencesErrorToast';
import { DEFAULT_QUANTITY_MODE, DRAWER_WIDTH } from './constants';
import { PRODUCT_DETAILS_SCHEMA } from './schemas';
import { generateInitialValues } from './helpers/generateInitialValues';
import {
  convertFrontendToBackendUOM,
  convertBackendToFrontendUOM
} from './helpers/uomMapper';
import {
  BACKEND_TO_FRONTEND_MODE,
  FRONTEND_TO_BACKEND_MODE
} from './helpers/modeMapper';
import ProductQuantitySection from './components/ProductQuantitySection';
import ProductCostSection from './components/ProductCostSection';
import ProductOtherSection from './components/ProductOtherSection';
import FormDrawerFooter from './components/FormDrawerFooter';

import './style.css';

interface EditProductDrawerProps {
  product: ProductWithPlanFields;
  visible: boolean;
  suppressAnimation?: boolean;
  mask?: boolean;
  zIndex?: number;
  onClose: () => void;
  onSave: (product: ProductWithPlanFields) => void;
  isEditing?: boolean;
  /** Plans-only (`PlanProductFields.applicationCount`) — Applications has no equivalent, since each Application is a single event. */
  showApplicationCount?: boolean;
  /** Plans-only — swaps Application Method/Target Pest for Timing/Timing Tag/Target Date, since a Plan's product estimate has no application-event details yet. */
  showTimingFields?: boolean;
}

const FillProductDetailsDrawer: React.FC<EditProductDrawerProps> = ({
  product,
  visible,
  suppressAnimation = false,
  mask,
  zIndex,
  onClose,
  onSave,
  isEditing,
  showApplicationCount = false,
  showTimingFields = false
}) => {
  const { values: applicationValues } = useFormikContext<FormValues>();

  // Get organization context (resource-level preferences use orgId + resourceId only)
  const [{ organization }] = useContext(Context) as any;
  const { findPreferenceByKey, upsertPreference } = usePreferences();
  const formikRef = useRef<FormikProps<ProductWithPlanFields>>(null);
  const hasAttemptedLoadRef = useRef<string | null>(null);

  const { displayPreferenceErrorToast } = useHandlePreferencesErrorToast();

  const getInitialQuantityMode = () => {
    /**
     * "Rate per Acre" will be automatically selected as the default enterable input for Product Quantity.
     *  We will remember within the session which entry method they've selected.
     */
    const sessionMode = sessionStorage.getItem('productQuantityMode');
    return sessionMode || DEFAULT_QUANTITY_MODE;
  };

  const initialValues = generateInitialValues(
    applicationValues,
    product,
    getInitialQuantityMode()
  );

  const texts = LayoutStrings.products.productDetails;

  // Helper function to check if preferences should be loaded
  const shouldLoadPreferences = () => {
    if (!visible || !product.id || product.ratePerAcre) return false;
    const attemptKey = `${product.id}-${visible}`;
    return hasAttemptedLoadRef.current !== attemptKey;
  };

  // Helper function to restore preference values to form
  const restorePreferenceValues = (preferenceValue: any) => {
    if (!formikRef.current) return;

    const {
      productQuantityType,
      ratePerAcre,
      ratePerAcreUnit,
      totalProduct,
      totalProductUnit,
      ratePerTankUnit
    } = preferenceValue;

    const { setFieldValue } = formikRef.current;
    const frontendMode = BACKEND_TO_FRONTEND_MODE[productQuantityType];

    if (frontendMode) setFieldValue('quantityMode', frontendMode);
    if (ratePerAcre) setFieldValue('ratePerAcre', ratePerAcre);
    // The Total Product mode's own saved value was never restored — only
    // its unit was (`totalProductUnit`, converted below). Symmetrical with
    // the `ratePerAcre` restore above. Nullish, not truthy: a product can be
    // saved with an explicit 0 Total Product.
    if (totalProduct != null) setFieldValue('totalProduct', totalProduct);

    // Convert backend UOM to frontend format
    const convertedRatePerAreaUnit = convertBackendToFrontendUOM(
      ratePerAcreUnit
    );
    const convertedRatePerTankUnit = convertBackendToFrontendUOM(
      ratePerTankUnit
    );
    const convertedTotalProductUnit = convertBackendToFrontendUOM(
      totalProductUnit
    );

    if (convertedRatePerAreaUnit) {
      setFieldValue('ratePerAreaUnit', convertedRatePerAreaUnit);
    }
    if (convertedRatePerTankUnit) {
      setFieldValue('ratePerTankUnit', convertedRatePerTankUnit);
    }
    if (convertedTotalProductUnit) {
      setFieldValue('totalProductUnit', convertedTotalProductUnit);
    }

    // Store preference data to trigger calculations later
    setFieldValue('_preferenceData', {
      mode: frontendMode,
      ratePerAcre,
      convertedRatePerAreaUnit,
      convertedRatePerTankUnit,
      convertedTotalProductUnit
    });
  };

  // Load product preferences when drawer opens (only for new products)
  useEffect(() => {
    const loadProductPreferences = async () => {
      if (!shouldLoadPreferences()) return;

      const attemptKey = `${product.id}-${visible}`;

      try {
        const productPreference = await findPreferenceByKey(
          PreferenceKey.OPERATION_PRODUCT,
          {
            orgId: organization.id,
            resourceId: product.id
          }
        );

        hasAttemptedLoadRef.current = attemptKey;

        if (productPreference?.preferenceValue) {
          restorePreferenceValues(productPreference.preferenceValue);
        }
      } catch (error) {
        hasAttemptedLoadRef.current = attemptKey;

        // Log to Datadog
        datadogLogs.logger.error(
          'Failed to load product preferences in FillProductDetailsDrawer',
          {
            error,
            productId: product.id,
            orgId: organization.id
          }
        );

        displayPreferenceErrorToast();
      }
    };

    loadProductPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, product.id, organization.id]);

  // Handle save with preference storage
  const handleSave = async (productValues: ProductWithPlanFields) => {
    // Only save preferences if we have valid IDs (resource-level needs orgId + resourceId)
    if (organization?.id && product.id) {
      try {
        await upsertPreference({
          preferenceKey: PreferenceKey.OPERATION_PRODUCT,
          preferenceValue: {
            productQuantityType:
              FRONTEND_TO_BACKEND_MODE[productValues.quantityMode!],
            ratePerAcre: Number(productValues.ratePerAcre),
            ratePerAcreUnit: convertFrontendToBackendUOM(
              productValues.ratePerAreaUnit
            ),
            // Saved alongside ratePerAcre so a product last entered in Total
            // Product mode restores that value too, not just its unit.
            totalProduct: Number(productValues.totalProduct),
            totalProductUnit: convertFrontendToBackendUOM(
              productValues.totalProductUnit
            ),
            // Plans has no tank/carrier concept, so a Plans-originated
            // product never has `ratePerTankUnit` set. Fall back to the
            // product's own catalog unit (`stdunit`), same as
            // `generateInitialValues` already does when seeding this field.
            ratePerTankUnit: convertFrontendToBackendUOM(
              productValues.ratePerTankUnit || product.stdunit
            )
          },
          orgId: organization.id,
          resourceId: product.id
        });
      } catch (error) {
        // Don't block save if preference fails, but log to Datadog
        datadogLogs.logger.error(
          'Failed to save product preferences in FillProductDetailsDrawer',
          {
            error,
            productId: product.id,
            orgId: organization.id,
            quantityMode: productValues.quantityMode
          }
        );
      }
    }

    // Call the original onSave
    onSave({
      ...productValues,
      registrationNumber:
        productValues.registrationNumber ||
        productValues.registrationnumber ||
        ''
    });
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={PRODUCT_DETAILS_SCHEMA}
      onSubmit={handleSave}
      enableReinitialize
    >
      {() => {
        return (
          <Drawer
            zIndex={zIndex}
            className={`product-drawer ${
              suppressAnimation ? 'instant-drawer' : ''
            }`}
            open={visible}
            placement="right"
            closable={false}
            mask={mask} // When false, caller manages dimming externally to avoid flicker during drawer swaps
            maskClosable={false}
            onClose={onClose}
            width={DRAWER_WIDTH}
            footer={
              <FormDrawerFooter
                cancelText={isEditing ? texts.cancelEdition : texts.backButton}
                cancelIcon={<ChevronLeftIcon />}
                submitText={isEditing ? texts.updateButton : texts.saveButton}
                onClose={onClose}
              />
            }
          >
            <div className="relative">
              <div className="absolute top-0 right-0 m-3">
                <Button
                  type={ButtonType.link}
                  aria-label="Close product details"
                  onClick={onClose}
                  icon={<CloseIcon aria-hidden="true" />}
                />
              </div>
            </div>
            <div className="product-form px-8 pt-8">
              <div className="gap-4 mb-4" data-testid="product-container">
                <h1 className="text-xl font-semibold block leading-snug">
                  {product.name}
                </h1>
                <p className="text-normal text-neutral-60">
                  {product.manufacturer}
                </p>
              </div>
              <div className="mb-6 cwf-product-drawer-form-content">
                <ProductQuantitySection
                  product={product}
                  application={applicationValues}
                />
                <ProductCostSection
                  product={product}
                  application={applicationValues}
                />
                <ProductOtherSection
                  application={applicationValues}
                  showApplicationCount={showApplicationCount}
                  showTimingFields={showTimingFields}
                />
              </div>
            </div>
          </Drawer>
        );
      }}
    </Formik>
  );
};

export default FillProductDetailsDrawer;
